/**
 * V2's canonical message is an ordered `ApiMessagePart[]` keyed by `seq`. This reducer is
 * shared by live streaming and admin replay and stores only parts plus run/control state.
 * Markdown, references, files, context, and render items are derived from the ordered parts
 * by the selectors below.
 */
import { ApiStreamEventType } from "../marvin/streamProtocol";
import type { ApiStreamEvent, ApiDebugStreamEvent, RunStatusEventData } from "../api/run";
import {
    parseApiMessagePart,
    type ActivityPartPayload,
    type ApiMessagePart,
    type ReferencePartPayload,
    type TextPartPayload,
} from "../marvin/messageParts";
import type { ApiFile, ApiReference, ArtifactMeta } from "../shared/interfaces";

export type {
    MarvinAction,
    MarvinMemoryUpdate,
    MarvinNavigationSuggestion,
    MarvinQuestion,
} from "../marvin/messageParts";

// ---------------------------------------------------------------------------
// Legacy display types (retained for the activity-trace persistence seam)
// ---------------------------------------------------------------------------

/**
 * A finalized line of streaming progress. Now purely a display/persistence shim: the live
 * source of truth is the ordered `activity` parts, but the activity-trace persistence seam
 * (Conversation → ConversationApi.saveMessageTrace → ApiMessage.trace) and legacy
 * (pre-parts) messages still speak `ProgressLine`, so activity parts convert to/from it.
 */
export interface ProgressLine {
    id: string;
    kind: "thinking" | "tool" | "result";
    text: string;
    toolName?: string;
    /** The helper this line belongs to: `helperId` on current servers, `researchId` on older records
     * (current servers write both). Group by `helperIdOf`, never by one of them. */
    researchId?: string;
    helperId?: string;
    helperName?: string;
    excerptCount?: number | null;
    /** Which reasoning segment a thinking line belongs to (groups the activity timeline).
     * Undefined on part-derived lines — buildActivityPhases then merges consecutive. */
    segment?: number;
    /** Legacy interleave anchor (body length when the line landed). Order now comes from
     * `seq`, so this is vestigial; kept for the persisted-trace wire shape. */
    contentOffset?: number;
}

/** Full state of the run stream: the ordered parts + run/control state. */
export interface RunStreamState {
    /** The canonical ordered message parts, by `seq`. */
    parts: ApiMessagePart[];
    /** Whether the stream is currently active. */
    isStreaming: boolean;
    /** Whether the stream has completed. */
    isDone: boolean;
    /** Final status of the run. */
    status: "ok" | "cancelled" | "error" | null;
    /** Error message if the run failed. */
    error: string | null;
    /** The run ID. */
    runId: string | null;
    /** The Marvin message ID. */
    marvinMessageId: string | null;
    /** The user's query being processed. */
    userQuery: string | null;
    /** Last processed stream-event envelope seq — dedups reconnect replay at the event
     * level (a lower/equal seq was already applied). Part-level idempotence (a re-delivered
     * part_created for an existing part seq) is enforced independently in upsertPart. */
    lastSeq: number;
    /** The run waits for a free Marvin: its place and the Marvins working for this person.
     * Null once it starts, and for a run that never waited. */
    queue: Extract<RunStatusEventData, { state: "queued" }> | null;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

export const initialStreamState: RunStreamState = {
    parts: [],
    isStreaming: false,
    isDone: false,
    status: null,
    error: null,
    runId: null,
    marvinMessageId: null,
    userQuery: null,
    lastSeq: 0,
    queue: null,
};

// ---------------------------------------------------------------------------
// Part mutations (all pure; return a new parts array)
// ---------------------------------------------------------------------------

/**
 * Insert a part in `seq` order, or REPLACE the existing part with the same seq. Idempotent
 * by seq: a reconnect replay (or a snapshot/tail overlap) re-delivering `part_created` for
 * a seq we already hold replaces it in place instead of appending a duplicate.
 */
function upsertPart(parts: ApiMessagePart[], part: ApiMessagePart): ApiMessagePart[] {
    const idx = parts.findIndex((p) => p.seq === part.seq);
    if (idx !== -1) {
        const next = parts.slice();
        next[idx] = part;
        return next;
    }
    // Parts almost always arrive in seq order, so scan from the end for the insert point.
    let i = parts.length;
    while (i > 0 && parts[i - 1].seq > part.seq) i--;
    const next = parts.slice();
    next.splice(i, 0, part);
    return next;
}

/** Apply a `part_updated` to the part with `seq`: append to the streaming text payload, or
 * full-replace a non-text payload. No-op if the part isn't present yet (the enclosing
 * `part_created` always carries a lower envelope seq, so it is applied first). */
function updatePart(
    parts: ApiMessagePart[],
    seq: number,
    change: { payload?: Record<string, unknown>; appendText?: string },
): ApiMessagePart[] {
    const idx = parts.findIndex((p) => p.seq === seq);
    if (idx === -1) return parts;
    const existing = parts[idx];
    let payload: Record<string, unknown>;
    if (typeof change.appendText === "string") {
        const prev = (existing.payload as unknown as TextPartPayload).text ?? "";
        payload = { ...existing.payload, text: prev + change.appendText };
    } else if (change.payload) {
        payload = change.payload;
    } else {
        return parts;
    }
    const parsed = parseApiMessagePart({ ...existing, payload });
    if (!parsed) return parts;
    const next = parts.slice();
    next[idx] = parsed;
    return next;
}

/** Flip a part's status to `complete`. No-op if absent. */
function completePart(parts: ApiMessagePart[], seq: number): ApiMessagePart[] {
    const idx = parts.findIndex((p) => p.seq === seq);
    if (idx === -1 || parts[idx].status === "complete") return parts;
    const next = parts.slice();
    next[idx] = { ...next[idx], status: "complete" };
    return next;
}

// ---------------------------------------------------------------------------
// Event Conversion
// ---------------------------------------------------------------------------

/** Convert a debug event (from the database) to a stream event (for the reducer). */
export function toApiStreamEvent(debugEvent: ApiDebugStreamEvent): ApiStreamEvent {
    return debugEvent.eventJson as unknown as ApiStreamEvent;
}

export type StreamProtocol = "parts" | "legacy";

/**
 * Identifies the content protocol from an event that exists in only one wire format.
 * Control/status events return null because both legacy and parts streams emit them.
 */
export function streamProtocolForEventType(type: ApiStreamEventType): StreamProtocol | null {
    switch (type) {
        case ApiStreamEventType.PartCreated:
        case ApiStreamEventType.PartUpdated:
        case ApiStreamEventType.PartCompleted:
            return "parts";
        case ApiStreamEventType.ThinkingStart:
        case ApiStreamEventType.ThinkingDelta:
        case ApiStreamEventType.ThinkingStop:
        case ApiStreamEventType.ReferencesStart:
        case ApiStreamEventType.ReferencesDelta:
        case ApiStreamEventType.ReferencesStop:
        case ApiStreamEventType.ContentBlockStart:
        case ApiStreamEventType.ContentBlockDelta:
        case ApiStreamEventType.ContentBlockStop:
        case ApiStreamEventType.ToolCallStart:
        case ApiStreamEventType.ToolCallDelta:
        case ApiStreamEventType.ToolCallStop:
        case ApiStreamEventType.ToolResult:
        case ApiStreamEventType.ArtifactReady:
        case ApiStreamEventType.AskUser:
        case ApiStreamEventType.AskUserAnswered:
        case ApiStreamEventType.ActionProposed:
        case ApiStreamEventType.ActionResolved:
        case ApiStreamEventType.MemoryUpdated:
        case ApiStreamEventType.NavigationSuggested:
        case ApiStreamEventType.ContextStats:
            return "legacy";
        default:
            return null;
    }
}

/** Chooses one reducer for a persisted run. Parts wins for any mixed/cutover history. */
export function detectStreamProtocol(events: ApiDebugStreamEvent[]): StreamProtocol {
    let detected: StreamProtocol | null = null;
    for (const event of events) {
        const protocol = streamProtocolForEventType(event.eventType);
        if (protocol === "parts") return protocol;
        if (protocol === "legacy") detected = protocol;
    }
    return detected ?? "parts";
}

// ---------------------------------------------------------------------------
// Reducer
// ---------------------------------------------------------------------------

/** Handle a single stream event and return the new state. */
export function handleStreamEvent(state: RunStreamState, event: ApiStreamEvent): RunStreamState {
    // Envelope-seq dedup: skip events already processed (handles reconnect replay).
    if (event.seq > 0 && event.seq <= state.lastSeq) {
        return state;
    }
    const baseState = event.seq > 0 ? { ...state, lastSeq: event.seq } : state;

    switch (event.type) {
        case ApiStreamEventType.PartCreated: {
            const part = parseApiMessagePart(event.data.part);
            return part ? { ...baseState, parts: upsertPart(baseState.parts, part) } : baseState;
        }

        case ApiStreamEventType.PartUpdated:
            return {
                ...baseState,
                parts: updatePart(baseState.parts, event.data.seq, {
                    payload: event.data.payload,
                    appendText: event.data.appendText,
                }),
            };

        case ApiStreamEventType.PartCompleted:
            return { ...baseState, parts: completePart(baseState.parts, event.data.seq) };

        case ApiStreamEventType.Error:
            return { ...baseState, error: event.data.error };

        case ApiStreamEventType.RunStatus:
            return { ...baseState, queue: event.data.state === "queued" ? event.data : null };

        case ApiStreamEventType.Done:
            // The consumer owns closing UI parts: a cancelled or failed run sends
            // no part_completed, so DONE closes anything still streaming.
            return {
                ...baseState,
                parts: baseState.parts.some((p) => p.status === "streaming")
                    ? baseState.parts.map((p) =>
                          p.status === "streaming" ? { ...p, status: "complete" as const } : p,
                      )
                    : baseState.parts,
                isStreaming: false,
                isDone: true,
                // `joined` ends a waiting run whose question the conversation's live run read:
                // its answer is that run's, finished like any other.
                status: event.data.status === "joined" ? "ok" : event.data.status,
                queue: null,
            };

        // Control/status events that carry no part: they only advance lastSeq. Legacy
        // 28-type content events (suppressed post-cutover, but a pre-cutover replay in the
        // admin view may still carry them) are intentionally ignored here — the parts model
        // supersedes them.
        case ApiStreamEventType.Step:
        case ApiStreamEventType.Retrieval:
        case ApiStreamEventType.LlmStart:
        case ApiStreamEventType.LlmStop:
        case ApiStreamEventType.ToolSuggestion:
        case ApiStreamEventType.ThinkingStart:
        case ApiStreamEventType.ThinkingDelta:
        case ApiStreamEventType.ThinkingStop:
        case ApiStreamEventType.ContentBlockStart:
        case ApiStreamEventType.ContentBlockDelta:
        case ApiStreamEventType.ContentBlockStop:
        case ApiStreamEventType.ToolCallStart:
        case ApiStreamEventType.ToolCallDelta:
        case ApiStreamEventType.ToolCallStop:
        case ApiStreamEventType.ToolResult:
        case ApiStreamEventType.ReferencesStart:
        case ApiStreamEventType.ReferencesDelta:
        case ApiStreamEventType.ReferencesStop:
        case ApiStreamEventType.ArtifactReady:
        case ApiStreamEventType.AskUser:
        case ApiStreamEventType.AskUserAnswered:
        case ApiStreamEventType.ActionProposed:
        case ApiStreamEventType.ActionResolved:
        case ApiStreamEventType.MemoryUpdated:
        case ApiStreamEventType.NavigationSuggested:
        case ApiStreamEventType.ContextStats:
        case ApiStreamEventType.MessageJoined:
        case ApiStreamEventType.ToolTouched:
            // `message_joined` (a message sent mid-answer that this run read) is tracked by the
            // stream owner (hooks/useRunStream.ts), not here. `tool_touched` (the sections a step
            // read) reaches Marvin Explore folded into the activity part's `touched`; the frame
            // itself draws nothing (its fallback is "dropped").
            return baseState;

        default: {
            const unhandledType = (event as { type: string }).type;
            console.warn(`Unhandled stream event type: ${unhandledType}`, event);
            return baseState;
        }
    }
}

// ---------------------------------------------------------------------------
// Replay Helper
// ---------------------------------------------------------------------------

/** Build replay state by applying events up to a given index (admin replay view). */
export function buildReplayState(events: ApiDebugStreamEvent[], upToIndex: number): RunStreamState {
    let state = initialStreamState;
    for (let i = 0; i < upToIndex && i < events.length; i++) {
        state = handleStreamEvent(state, toApiStreamEvent(events[i]));
    }
    return state;
}

// ---------------------------------------------------------------------------
// Derived selectors (parts → what the surfaces render)
// ---------------------------------------------------------------------------

const EMPTY_FILES: ApiFile[] = [];

/** Concatenated markdown answer body: all `text` parts joined in seq order. */
export function getMarkdownContent(state: RunStreamState): string {
    return partsMarkdown(state.parts);
}

/** Concatenated markdown answer body from a raw parts list (live or hydrated). */
export function partsMarkdown(parts: ApiMessagePart[]): string {
    return parts
        .filter((p) => p.kind === "text")
        .map((p) => (p.payload as unknown as TextPartPayload).text || "")
        .join("");
}

/** Merged citation reference map across all `reference` parts, in seq order. */
export function partsReferenceMap(parts: ApiMessagePart[]): Record<string, ApiReference> {
    const map: Record<string, ApiReference> = {};
    for (const part of parts) {
        if (part.kind !== "reference") continue;
        Object.assign(map, (part.payload as unknown as ReferencePartPayload).references || {});
    }
    return map;
}

/** Files associated with references, concatenated across `reference` parts (deduped by id). */
export function partsFiles(parts: ApiMessagePart[]): ApiFile[] {
    const files: ApiFile[] = [];
    const seen = new Set<string>();
    for (const part of parts) {
        if (part.kind !== "reference") continue;
        for (const file of (part.payload as unknown as ReferencePartPayload).files || []) {
            if (file?.id && seen.has(file.id)) continue;
            if (file?.id) seen.add(file.id);
            files.push(file);
        }
    }
    return files.length ? files : EMPTY_FILES;
}

/** Artifacts emitted this turn (`artifact` parts), deduped by id. */
export function partsArtifacts(parts: ApiMessagePart[]): ArtifactMeta[] {
    const out: ArtifactMeta[] = [];
    const seen = new Set<string>();
    for (const part of parts) {
        if (part.kind !== "artifact") continue;
        const meta = part.payload as unknown as ArtifactMeta;
        if (meta?.id && seen.has(meta.id)) continue;
        if (meta?.id) seen.add(meta.id);
        out.push(meta);
    }
    return out;
}

/** The helper a line or part belongs to: `helperId`, else the `researchId` older records carry. */
export function helperIdOf(item: { helperId?: string; researchId?: string }): string | undefined {
    return item.helperId || item.researchId || undefined;
}

/** The `activity` parts as ProgressLine[] — the shape the persisted activity trace uses.
 * Order is the parts' seq order. A `result` line's excerptCount is parsed from its text. */
export function activityPartsToTrace(parts: ApiMessagePart[]): ProgressLine[] {
    const lines: ProgressLine[] = [];
    for (const part of parts) {
        if (part.kind !== "activity") continue;
        const p = part.payload as unknown as ActivityPartPayload;
        lines.push({
            id: `p${part.seq}`,
            kind: p.kind,
            // A `tool` line displays the backend-built rich label ("Searching specs for '…'",
            // the research query), falling back to the bare tool name (formatter-less tools)
            // then the raw text (legacy traces store the label in `text`). `thinking`/`result`
            // lines keep their text verbatim (a `result` line's raw text feeds the excerpt count).
            text: p.kind === "tool" ? (p.label ?? p.toolName ?? p.text) : p.text,
            toolName: p.toolName,
            researchId: p.researchId,
            helperId: p.helperId,
            helperName: p.helperName,
            excerptCount:
                p.kind === "result" ? parseResearchExcerptCount(p.text, p.toolName) : undefined,
            contentOffset: p.contentOffset,
        });
    }
    return lines;
}

/** Synthesize a parts list for a LEGACY (pre-parts) message from its flat fields, so the
 * one renderer handles it too: a reference part (so citations still resolve), the persisted
 * activity trace as activity parts, then the answer as one text part. Card synthesis is
 * omitted — legacy messages predate the cards and hydrate no parts. */
export function legacyMessageToParts(
    message: string,
    trace: ProgressLine[] | undefined,
    references?: Record<string, ApiReference>,
    files?: ApiFile[],
    artifacts?: ArtifactMeta[],
): ApiMessagePart[] {
    const parts: ApiMessagePart[] = [];
    let seq = 0;
    if (references && Object.keys(references).length > 0) {
        parts.push({
            seq: seq++,
            kind: "reference",
            payload: { references, files: files || [] } as unknown as Record<string, unknown>,
            status: "complete",
        });
    }
    for (const line of trace || []) {
        const payload: ActivityPartPayload = { kind: line.kind, text: line.text };
        if (line.toolName) payload.toolName = line.toolName;
        // A persisted trace stores a tool line's display label in `text`; carry it back as the
        // part's `label` so re-derivation (activityPartsToTrace) keeps the rich label instead
        // of collapsing to the bare tool name on "open in full view" replays.
        if (line.kind === "tool") payload.label = line.text;
        if (line.researchId) payload.researchId = line.researchId;
        if (line.helperId) payload.helperId = line.helperId;
        if (line.helperName) payload.helperName = line.helperName;
        parts.push({
            seq: seq++,
            kind: "activity",
            payload: payload as unknown as Record<string, unknown>,
            status: "complete",
        });
    }
    if (message) {
        parts.push({ seq: seq++, kind: "text", payload: { text: message }, status: "complete" });
    }
    for (const artifact of artifacts || []) {
        parts.push({
            seq: seq++,
            kind: "artifact",
            payload: artifact as unknown as Record<string, unknown>,
            status: "complete",
        });
    }
    return parts;
}

// ---------------------------------------------------------------------------
// Research subagent helpers
// ---------------------------------------------------------------------------

/**
 * Excerpt count from a research `search_documents` result line's text, or `null` when it
 * can't be read cleanly (the research block then falls back to a searches-only summary —
 * never an invented number). The result text opens with `Found N results…` / `Found N
 * RFIs:`; an explicit "No … found" means zero.
 */
export function parseResearchExcerptCount(
    result: string | undefined,
    toolName: string | undefined,
): number | null {
    if (toolName !== "search_documents" || !result) return null;
    const found = /^\s*Found\s+(\d+)\b/i.exec(result);
    if (found) return Number(found[1]);
    if (/^\s*No\b[^\n]*\b(?:found|documents)\b/i.test(result)) return 0;
    return null;
}

// ---------------------------------------------------------------------------
// Activity timeline grouping
// ---------------------------------------------------------------------------

/** A single tool call in the activity timeline (a row under a phase). */
interface ActivityTool {
    id: string;
    label: string;
    toolName: string;
}

/** A "phase" of the turn: one reasoning summary heading the tool calls that followed it. */
export interface ActivityPhase {
    id: string;
    summary: string;
    tools: ActivityTool[];
}

/**
 * Group a contiguous run of MAIN-agent activity lines into phases for the timeline. A
 * reasoning line opens a NEW phase when it belongs to a different reasoning segment (or
 * tool calls already attached to the current phase); same-segment lines just advance the
 * heading. Helper-tagged lines are excluded (they render in their own block). Order is
 * preserved.
 */
export function buildActivityPhases(lines: ProgressLine[]): ActivityPhase[] {
    const phases: ActivityPhase[] = [];
    let current: ActivityPhase | null = null;
    let currentSegment: number | undefined;

    for (const line of lines) {
        if (helperIdOf(line)) continue;
        if (line.kind === "thinking") {
            if (!current || current.tools.length > 0 || line.segment !== currentSegment) {
                current = { id: line.id, summary: line.text, tools: [] };
                currentSegment = line.segment;
                phases.push(current);
            } else {
                current.summary = line.text;
            }
        } else {
            if (!current) {
                current = { id: line.id, summary: "", tools: [] };
                currentSegment = undefined;
                phases.push(current);
            }
            current.tools.push({ id: line.id, label: line.text, toolName: line.toolName ?? "" });
        }
    }
    return phases;
}
